"use client";
import { useRouter, useSearchParams } from "next/navigation";
import Image from "next/image";
import { useState, useTransition } from "react";
import Tabs from "@/components/custom-tabs";
import SupplyCard from "@/components/supply-card";
import DemandCard from "@/components/demand-card";
import CustomPagination from "@/components/card-pagination";
import { ImageIcon } from "lucide-react";
import Breadcrumb from "@/components/bread-crumb";
import { FallbackAvatar } from "@/components/fallback-avatar";

type AdType = "supply" | "demand";

type Props = {
  supply: any[];
  demand: any[];
  supplyCount: number;
  demandCount: number;
  page: number;
  pageSize: number;
  activeTab: any;
  consultant: any;
};

const FilesByConsultantId = ({
  supply,
  demand,
  supplyCount,
  demandCount,
  page,
  pageSize,
  activeTab: initialTab,
  consultant,
}: Props) => {
  const router = useRouter();
  const searchParams = useSearchParams();

  const [isPending, startTransition] = useTransition();

  const [activeTab, setActiveTab] = useState<AdType>(
    initialTab === "demand" ? "demand" : "supply"
  );

  const currentData = activeTab === "supply" ? supply : demand;
  const count = activeTab === "supply" ? supplyCount : demandCount;

  const handleTabChange = (id: string) => {
    const nextTab = id as AdType;
    setActiveTab(nextTab);

    const params = new URLSearchParams(searchParams.toString());
    params.set("tab", nextTab);
    params.set("page", "1");

    startTransition(() => {
      router.push(`?${params.toString()}`, { scroll: false });
    });
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set("page", String(newPage + 1));

    startTransition(() => {
      router.push(`?${params.toString()}`, { scroll: false });
    });
  };

  const handlePageSizeChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set("size", event.target.value);
    params.set("page", "1");

    startTransition(() => {
      router.push(`?${params.toString()}`, { scroll: false });
    });
  };

  const tabsData = [
    { id: "supply", label: "عرضه" },
    { id: "demand", label: "درخواستی" },
  ];
  const consultantName = consultant?.name || "";

  return (
    <>
      <Breadcrumb
        items={[
          {
            title: "مشاوران",
            route: "/customer/consultants",
          },
          {
            title: consultantName,
          },
        ]}
      />

      <div className="w-full flex flex-col gap-3 mt-4 mb-8 bg-white  rounded-2xl">
        {consultant && (
          <div
            key={consultant.id}
            className="bg-white rounded-2xl  flex items-center gap-6 w-full"
          >
            <FallbackAvatar
              src={consultant.avatar}
              name={consultant.name}
              id={consultant.id}
              alt={consultant.name}
              className="w-24 h-24 shrink-0"
              textClassName="text-3xl"
            />

            {/* Details */}
            <div className="flex flex-col gap-3 flex-1 text-start">
              {consultant?.name && (
                <h3 className="text-lg font-semibold text-gray-900">
                  {consultant?.name}
                </h3>
              )}
              <div className="flex flex-wrap items-center gap-4">
                {/* File Count */}
                <span className="flex items-center gap-1 text-gray-500 text-sm sm:text-base">
                  <ImageIcon size={22} color="#99a1af" />
                  {" "}
                  <span className="text-base font-bold text-gray-900 mr-2">
                    {consultant?.file_count}
                  </span>
                  <span>فایل فعال</span>
                </span>

                {consultant?.phone && (
                  <a
                    href={`tel:${consultant?.phone}`}
                    className="inline-flex
                              items-center
                              justify-center
                              rounded-full
                              bg-primary
                              px-6
                              py-2
                              text-sm
                              sm:text-base
                              font-medium
                              text-white
                              whitespace-nowrap
                              transition-all
                              hover:bg-[#3730d8]
                              hover:shadow-xl
                              hover:shadow-primary/30
                              active:scale-95
                              cursor-pointer"
                  >
                    {consultant?.phone}
                  </a>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-col gap-3 mt-7 mb-3 flex-1 text-start">
        <h3 className="text-lg font-semibold text-gray-900">
          آگهی های این مشاور
        </h3>
      </div>
      <Tabs
        tabs={tabsData}
        activeTabId={activeTab}
        onTabChange={handleTabChange}
      />

      {isPending ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mt-6">
          {Array.from({ length: pageSize }, (_, i) => i).map((slot) => (
            <div key={`skeleton-${slot}`} className="animate-pulse rounded-2xl  border border-gray-300 p-4 space-y-3">
              <div className="h-5 w-24 bg-gray-200 rounded"></div>

              <div className="w-full h-48 bg-gray-200 rounded-xl"></div>

              <div className="space-y-2">
                <div className="h-4 w-3/4 bg-gray-200 rounded"></div>
                <div className="h-4 w-1/2 bg-gray-200 rounded"></div>
              </div>

              <div className="h-5 w-32 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
      ) : (
        <>
          {activeTab === "supply" && (
            <div id="supply-card-grid" className="grid gap-6 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {supply?.map((ad: any) => (
                <SupplyCard key={ad.id || ad.ad_id} ad={ad} />
              ))}
            </div>
          )}
          {activeTab === "demand" && (
            <div id="demand-card-grid" className="grid grid-cols-1 gap-6 lg:grid-cols-4 md:grid-cols-3  sm:grid-cols-2">
              {demand?.map((ad: any) => (
                <DemandCard key={ad.id || ad.ad_id} ad={ad} />
              ))}
            </div>
          )}
        </>
      )}

      {!isPending && !currentData.length && (
        <div className="flex w-full justify-center items-center flex-col md:flex-row gap-10">
          <div className="pt-12 pl-10 text-center text-gray-500 text-lg font-medium">
            متاسفانه نتیجه‌ای پیدا نشد
          </div>

          <div className="relative md:w-125 md:h-125 w-75 h-75">
            <Image
              src="/assets/empty-properties.svg"
              alt="property ads not found"
              fill
              sizes="256px"
              style={{ objectFit: "contain" }}
            />
          </div>
        </div>
      )}

      <CustomPagination
        page={page}
        pageSize={pageSize}
        count={count}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />
    </>
  );
};

export default FilesByConsultantId;
